import type React from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import loadScript from './loadScript';
import styles from './LocationInput.module.css';
import inputStyles from '../shared/Input.module.css';
import CountryDropdown from '../CountryDropdown';

type Address = {
  address?: string;
  city?: string;
  zip_code?: string;
  country?: string;
}

interface Props {
  values: Address,
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
  setNewValues: (key: string, value: string) => void,
  className?: string,
  state: string,
  hint?: string,
}

export default function LocationInput({
  values, handleChange, setNewValues, className, state, hint,
}: Props) {
  let autoComplete: any;
  const autoCompleteRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const handlePlaceSelect = async () => {
    const addressObject = autoComplete.getPlace();
    const newAddress = `${addressObject.address_components.find((details: any) => details.types.includes('street_number'))?.long_name
      || addressObject.address_components.find((details: any) => details.types.includes('political'))?.long_name
      || addressObject.address_components.find((details: any) => details.types.includes('locality'))?.long_name
      || values.address
      } ${addressObject.address_components.find((details: any) => details.types.includes('route'))?.long_name || ''}`.trim();
    setNewValues('address', newAddress);

    setNewValues('zip_code', addressObject.address_components.find((details: any) => details.types.includes('postal_code'))?.long_name || '');
    setNewValues('city', addressObject.address_components.find((details: any) => details.types.includes('locality'))?.long_name || '');
    setNewValues('country', addressObject.address_components.find((details: any) => details.types.includes('country'))?.short_name || '');
  };

  const handleScriptLoad = () => {
    if (autoCompleteRef.current) {
      autoComplete = new window.google.maps.places.Autocomplete(
        autoCompleteRef.current,
      );
      autoComplete.setFields(['address_components', 'formatted_address']);
      autoComplete.addListener('place_changed', () => handlePlaceSelect());
    }
  };

  useEffect(() => {
    loadScript(
      'https://maps.googleapis.com/maps/api/js?key=AIzaSyDQyUNkEbrMMvJY-X0F6gx4y5B2KLv__U4&libraries=places',
      handleScriptLoad,
    );
  }, []);

  return (
    <div className={(state === 'ERROR') ? styles.error : ''}>
      <div className={className}>
        <div className={styles.inputContainer}>
          <p className={inputStyles.label}>
            {t('invoicingForm.address')}
          </p>
          <input
            className={inputStyles.input}
            name="address"
            ref={autoCompleteRef}
            onChange={handleChange}
            value={values.address || ''}
          />
        </div>
        <div className={styles.row}>
          <div className={styles.inputContainer}>
            <p className={inputStyles.label}>
              {t('invoicingForm.zipCode')}
            </p>
            <input
              type="text"
              name="zip_code"
              className={inputStyles.input}
              value={values.zip_code || ''}
              onChange={handleChange}
            />
          </div>
          <div className={styles.inputContainer}>
            <p className={inputStyles.label}>
              {t('invoicingForm.city')}
            </p>
            <input
              type="text"
              name="city"
              className={inputStyles.input}
              value={values.city || ''}
              onChange={handleChange}
            />
          </div>
          <div className={styles.inputContainer}>
            <p className={inputStyles.label}>
              {t('invoicingForm.country')}
            </p>
            <CountryDropdown
              className={styles.countryInput}
              value={values.country || ''}
              onChange={handleChange}
            />
          </div>
        </div>
      </div>
      {
        (hint)
        && (
          <p className={styles.hint}>
            {hint}
          </p>
        )
      }
    </div>
  );
}
